import styled from "styled-components";

import { PageBody } from "../PageBody";
import { Typography } from "../../atoms/Typography";
import { Button } from "../../atoms/Button";
import { Loading } from "../../atoms/Loading";
import { formatDateJST } from "../../../utils/date";

const ContentWrapper = styled.div`
  padding: 20px 40px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const CreateButtonWrapper = styled.div`
  width: 160px;
  height: 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 8px;
  border-bottom: 2px solid ${(props) => props.theme.colors.secondaryBorder};
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
  font-weight: bold;
`;

const Tr = styled.tr`
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.hoverBackground};
  }
`;

const Td = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
`;

const TermsBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.pageBodyBackground};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  font-size: 12px;
  margin-left: 8px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 8px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 4px;
  background: ${(props) =>
    props.$active
      ? props.theme.colors.brand
      : props.theme.colors.primaryBackground};
  color: ${(props) =>
    props.$active ? "white" : props.theme.colors.primaryText};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Ellipsis = styled.span`
  padding: 6px 4px;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
`;

/**
 * 表示するページ番号とエリプシス("...")の並びを計算する。
 * 現在ページの前後 1 ページ・先頭・末尾を常に表示し、それ以外は省略する。
 * 例: 10 ページ中 5 ページ目 → [1, "...", 4, 5, 6, "...", 10]
 */
const buildPageItems = (
  totalPages: number,
  currentPage: number,
): (number | "ellipsis")[] => {
  const MAX_ITEMS = 7; // 1 ... c-1 c c+1 ... last
  if (totalPages <= MAX_ITEMS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items: (number | "ellipsis")[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  if (start > 2) items.push("ellipsis");
  for (let p = start; p <= end; p++) items.push(p);
  if (end < totalPages - 1) items.push("ellipsis");
  items.push(totalPages);
  return items;
};

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${(props) => props.theme.colors.good};
`;

const PDT_CODE_LABELS: Record<string, string> = {
  "2184b7b9-2dba-4a97-8fff-ccd7d0c8c40d": "SC",
  "39dd9c55-343f-4810-ba8a-f417b07d6caf": "HT",
  "c1a2b5e0-c0a3-41c3-aadd-7e14043fedb3": "RELIVION",
  "d38a1261-c952-41a7-8333-251498ad2d7f": "HAUDY",
};

type Notification = {
  id: string;
  title: string;
  startFrom: string;
  endAt: string | null;
  pdtCodes: string[];
  roles: string[];
  positions: string[];
  termsVersionId: string | null;
  isEditable: boolean;
  createdAt: string;
};

export type NotificationListPageProps = {
  loading: boolean;
  notifications: Notification[];
  totalCount: number;
  currentPage: number;
  onRowClick: (id: string) => void;
  onCreateClick: () => void;
  onPageChange: (page: number) => void;
};

const formatDate = formatDateJST;

const formatPdtCodes = (codes: string[]) => {
  if (codes.length === 0) return "全製品";
  return codes.map((c) => PDT_CODE_LABELS[c] || c).join(", ");
};

export function NotificationListPage({
  loading,
  notifications,
  totalCount,
  currentPage,
  onRowClick,
  onCreateClick,
  onPageChange,
}: NotificationListPageProps) {
  const totalPages = Math.ceil(totalCount / 20);

  return (
    <PageBody title="お知らせ管理">
      <ContentWrapper>
        <HeaderWrapper>
          <CreateButtonWrapper>
            <Button buttonType="primary" onClick={onCreateClick}>
              <Typography size={1} bold>
                新規作成
              </Typography>
            </Button>
          </CreateButtonWrapper>
        </HeaderWrapper>

        {loading ? (
          <Loading />
        ) : notifications.length === 0 ? (
          <EmptyMessage>
            <Typography size={2}>お知らせがありません</Typography>
          </EmptyMessage>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>タイトル</Th>
                  <Th>対象製品</Th>
                  <Th>掲載開始</Th>
                  <Th>掲載終了</Th>
                  <Th>作成日</Th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((n) => (
                  <Tr key={n.id} onClick={() => onRowClick(n.id)}>
                    <Td>
                      {n.title}
                      {!n.isEditable && <TermsBadge>編集不可</TermsBadge>}
                    </Td>
                    <Td>{formatPdtCodes(n.pdtCodes)}</Td>
                    <Td>{formatDate(n.startFrom)}</Td>
                    <Td>{formatDate(n.endAt)}</Td>
                    <Td>{formatDate(n.createdAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <PaginationWrapper>
                <PageButton
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  前へ
                </PageButton>
                {buildPageItems(totalPages, currentPage).map((item, i) =>
                  item === "ellipsis" ? (
                    <Ellipsis key={`ellipsis-${i}`}>…</Ellipsis>
                  ) : (
                    <PageButton
                      key={item}
                      $active={item === currentPage}
                      onClick={() => onPageChange(item)}
                    >
                      {item}
                    </PageButton>
                  ),
                )}
                <PageButton
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  次へ
                </PageButton>
              </PaginationWrapper>
            )}
          </>
        )}
      </ContentWrapper>
    </PageBody>
  );
}
